import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import axios from 'axios';

import { permissionsApi } from '../../services/api/permissions.api';
import {
  ListUsuariosParams,
  usuariosApi,
} from '../../services/api/usuarios.api';
import { Permission } from '../../types/permission';
import {
  CreateUsuarioPayload,
  PaginatedUsuarios,
  UpdatePermissionsPayload,
  UpdateUsuarioPayload,
  UsuarioInterno,
  UsuariosFilters,
} from '../../types/usuario';

export type UsuarioMutation =
  | 'create'
  | 'update'
  | 'activate'
  | 'deactivate'
  | 'permissions';

export interface UsuariosState {
  items: UsuarioInterno[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  filters: UsuariosFilters;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
  selectedUserId: string | null;
  selectedDetail: UsuarioInterno | null;
  detailStatus: 'idle' | 'loading' | 'error';
  detailError: string | null;
  mutating: { id: string | null; action: UsuarioMutation } | null;
  mutationError: string | null;
  permissions: Permission[];
  permissionsStatus: 'idle' | 'loading' | 'error';
}

const initialState: UsuariosState = {
  items: [],
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  filters: {},
  status: 'idle',
  error: null,
  selectedUserId: null,
  selectedDetail: null,
  detailStatus: 'idle',
  detailError: null,
  mutating: null,
  mutationError: null,
  permissions: [],
  permissionsStatus: 'idle',
};

export const messageFromError = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string | string[] }
      | undefined;
    if (data?.message) {
      return Array.isArray(data.message) ? data.message.join('; ') : data.message;
    }
    if (error.response?.status === 401) return 'Sua sessão expirou.';
    if (error.code === 'ERR_NETWORK') {
      return 'Não foi possível conectar ao servidor.';
    }
  }
  return fallback;
};

export const fetchUsuariosThunk = createAsyncThunk<
  PaginatedUsuarios,
  ListUsuariosParams,
  { rejectValue: string }
>('usuarios/fetch', async (params, { rejectWithValue }) => {
  try {
    return await usuariosApi.list(params);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar usuários'));
  }
});

export const fetchUsuarioDetailThunk = createAsyncThunk<
  UsuarioInterno,
  string,
  { rejectValue: string }
>('usuarios/fetchDetail', async (id, { rejectWithValue }) => {
  try {
    return await usuariosApi.detail(id);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar usuário'));
  }
});

export const createUsuarioThunk = createAsyncThunk<
  UsuarioInterno,
  CreateUsuarioPayload,
  { rejectValue: string }
>('usuarios/create', async (payload, { rejectWithValue }) => {
  try {
    return await usuariosApi.create(payload);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Não foi possível criar usuário'));
  }
});

export const updateUsuarioThunk = createAsyncThunk<
  UsuarioInterno,
  { id: string; payload: UpdateUsuarioPayload },
  { rejectValue: string }
>('usuarios/update', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await usuariosApi.update(id, payload);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Não foi possível atualizar'));
  }
});

export const activateUsuarioThunk = createAsyncThunk<
  UsuarioInterno,
  string,
  { rejectValue: string }
>('usuarios/activate', async (id, { rejectWithValue }) => {
  try {
    return await usuariosApi.activate(id);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Não foi possível ativar'));
  }
});

export const deactivateUsuarioThunk = createAsyncThunk<
  UsuarioInterno,
  string,
  { rejectValue: string }
>('usuarios/deactivate', async (id, { rejectWithValue }) => {
  try {
    return await usuariosApi.deactivate(id);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Não foi possível desativar'));
  }
});

export const updatePermissionsThunk = createAsyncThunk<
  UsuarioInterno,
  { id: string; payload: UpdatePermissionsPayload },
  { rejectValue: string }
>('usuarios/updatePermissions', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await usuariosApi.updatePermissions(id, payload);
  } catch (e) {
    return rejectWithValue(
      messageFromError(e, 'Não foi possível atualizar as permissões'),
    );
  }
});

export const fetchPermissionsThunk = createAsyncThunk<
  Permission[],
  void,
  { rejectValue: string }
>('usuarios/fetchPermissions', async (_, { rejectWithValue }) => {
  try {
    return await permissionsApi.list();
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar permissões'));
  }
});

const usuariosSlice = createSlice({
  name: 'usuarios',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<UsuariosFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    clearFilters(state) {
      state.filters = {};
      state.page = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = Math.max(1, action.payload);
    },
    setLimit(state, action: PayloadAction<number>) {
      state.limit = action.payload;
      state.page = 1;
    },
    selectUser(state, action: PayloadAction<string | null>) {
      state.selectedUserId = action.payload;
      if (action.payload === null) {
        state.selectedDetail = null;
        state.detailStatus = 'idle';
        state.detailError = null;
        state.mutationError = null;
      }
    },
    clearMutationError(state) {
      state.mutationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsuariosThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchUsuariosThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.items = action.payload.data;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchUsuariosThunk.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? 'Erro ao carregar usuários';
      })

      .addCase(fetchUsuarioDetailThunk.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchUsuarioDetailThunk.fulfilled, (state, action) => {
        state.detailStatus = 'idle';
        state.selectedDetail = action.payload;
      })
      .addCase(fetchUsuarioDetailThunk.rejected, (state, action) => {
        state.detailStatus = 'error';
        state.detailError = action.payload ?? 'Erro ao carregar usuário';
      })

      .addCase(createUsuarioThunk.pending, (state) => {
        state.mutating = { id: null, action: 'create' };
        state.mutationError = null;
      })
      .addCase(createUsuarioThunk.fulfilled, (state) => {
        state.mutating = null;
      })
      .addCase(createUsuarioThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError = action.payload ?? 'Não foi possível criar usuário';
      })

      .addCase(updateUsuarioThunk.pending, (state, action) => {
        state.mutating = { id: action.meta.arg.id, action: 'update' };
        state.mutationError = null;
      })
      .addCase(updateUsuarioThunk.fulfilled, (state, action) => {
        state.mutating = null;
        state.selectedDetail = action.payload;
      })
      .addCase(updateUsuarioThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError = action.payload ?? 'Não foi possível atualizar';
      })

      .addCase(activateUsuarioThunk.pending, (state, action) => {
        state.mutating = { id: action.meta.arg, action: 'activate' };
        state.mutationError = null;
      })
      .addCase(activateUsuarioThunk.fulfilled, (state, action) => {
        state.mutating = null;
        state.selectedDetail = action.payload;
      })
      .addCase(activateUsuarioThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError = action.payload ?? 'Não foi possível ativar';
      })

      .addCase(deactivateUsuarioThunk.pending, (state, action) => {
        state.mutating = { id: action.meta.arg, action: 'deactivate' };
        state.mutationError = null;
      })
      .addCase(deactivateUsuarioThunk.fulfilled, (state, action) => {
        state.mutating = null;
        state.selectedDetail = action.payload;
      })
      .addCase(deactivateUsuarioThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError = action.payload ?? 'Não foi possível desativar';
      })

      .addCase(updatePermissionsThunk.pending, (state, action) => {
        state.mutating = { id: action.meta.arg.id, action: 'permissions' };
        state.mutationError = null;
      })
      .addCase(updatePermissionsThunk.fulfilled, (state, action) => {
        state.mutating = null;
        state.selectedDetail = action.payload;
      })
      .addCase(updatePermissionsThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError =
          action.payload ?? 'Não foi possível atualizar as permissões';
      })

      .addCase(fetchPermissionsThunk.pending, (state) => {
        state.permissionsStatus = 'loading';
      })
      .addCase(fetchPermissionsThunk.fulfilled, (state, action) => {
        state.permissionsStatus = 'idle';
        state.permissions = action.payload;
      })
      .addCase(fetchPermissionsThunk.rejected, (state) => {
        state.permissionsStatus = 'error';
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setPage,
  setLimit,
  selectUser,
  clearMutationError,
} = usuariosSlice.actions;

export default usuariosSlice.reducer;
